import React, { useEffect, useState, useMemo } from 'react';
import { Trash2, RotateCcw, AlertTriangle, Database, Clock, CheckSquare, Square, ChevronDown, ChevronUp, Truck, Users, Wrench, Calendar, ClipboardList, AlertCircle, Lock, KeyRound } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { cn, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';

// Access code for data management
const ACCESS_CODE = '2802';

// Category icons
const CATEGORY_ICONS = {
  vehicles: Truck,
  employees: Users,
  maintenance: Wrench,
  tasks: ClipboardList,
  schedules: Calendar,
  incidents: AlertCircle
};

// Format datetime
const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Calculate time remaining
const getTimeRemaining = (expiresAt) => {
  const now = new Date();
  const expires = new Date(expiresAt);
  const diff = expires - now;
  
  if (diff <= 0) return 'Expiré';
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  
  if (days > 0) {
    return `${days}j ${remainingHours}h restants`;
  }
  return `${hours}h restants`;
};

export default function DataManagement() {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [codeInput, setCodeInput] = useState(['', '', '', '']);
  const [codeError, setCodeError] = useState(false);
  const [categories, setCategories] = useState([]);
  const [backups, setBackups] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedBackups, setSelectedBackups] = useState([]);
  const [expandedBatches, setExpandedBatches] = useState({});
  const [isRestoring, setIsRestoring] = useState(false);
  
  // Permanent delete states
  const [permanentDeleteDialog, setPermanentDeleteDialog] = useState({
    isOpen: false,
    step: 'code', // 'code' or 'confirm'
    batchId: null,
    categoryId: null
  });
  const [permanentDeleteCode, setPermanentDeleteCode] = useState(['', '', '', '']);
  const [permanentDeleteCodeError, setPermanentDeleteCodeError] = useState(false);
  const [isPermanentlyDeleting, setIsPermanentlyDeleting] = useState(false);
  
  const { theme } = useTheme();
  const { user } = useAuth();

  useEffect(() => {
    if (isUnlocked) {
      fetchData();
    }
  }, [isUnlocked]);

  // Handle code input
  const handleCodeInput = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only digits
    
    const newCode = [...codeInput];
    newCode[index] = value.slice(-1); // Only last digit
    setCodeInput(newCode);
    setCodeError(false);
    
    // Auto-focus next input
    if (value && index < 3) {
      const nextInput = document.getElementById(`code-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
    
    // Check if code is complete
    const fullCode = newCode.join('');
    if (fullCode.length === 4) {
      if (fullCode === ACCESS_CODE) {
        setIsUnlocked(true);
        toast.success('Accès autorisé');
      } else {
        setCodeError(true);
        setCodeInput(['', '', '', '']);
        toast.error('Code incorrect');
        // Focus first input
        setTimeout(() => {
          const firstInput = document.getElementById('code-input-0');
          if (firstInput) firstInput.focus();
        }, 100);
      }
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codeInput[index] && index > 0) {
      const prevInput = document.getElementById(`code-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoriesRes, backupsRes] = await Promise.all([
        api.get('/data-management/categories'),
        api.get('/data-management/backups')
      ]);
      setCategories(categoriesRes.data);
      setBackups(backupsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!selectedCategory) return;
    
    setIsDeleting(true);
    try {
      const response = await api.post(`/data-management/delete/${selectedCategory}`);
      toast.success(response.data.message);
      setDeleteDialogOpen(false);
      setSelectedCategory('');
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleBackupSelection = (backupId) => {
    setSelectedBackups(prev => 
      prev.includes(backupId) 
        ? prev.filter(id => id !== backupId)
        : [...prev, backupId]
    );
  };

  const toggleBatchExpanded = (batchId) => {
    setExpandedBatches(prev => ({
      ...prev,
      [batchId]: !prev[batchId]
    }));
  };

  const selectAllInBatch = (items) => {
    const ids = items.map(item => item.backup_id);
    const allSelected = ids.every(id => selectedBackups.includes(id));
    
    if (allSelected) {
      setSelectedBackups(prev => prev.filter(id => !ids.includes(id)));
    } else {
      setSelectedBackups(prev => [...new Set([...prev, ...ids])]);
    }
  };

  const handleRestoreSelected = async () => {
    if (selectedBackups.length === 0) {
      toast.error('Sélectionnez des éléments à restaurer');
      return;
    }
    
    setIsRestoring(true);
    try {
      const response = await api.post('/data-management/restore', selectedBackups);
      toast.success(response.data.message);
      setSelectedBackups([]);
      fetchData();
    } catch (error) {
      console.error('Error restoring:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la restauration');
    } finally {
      setIsRestoring(false);
    }
  };

  const handleRestoreBatch = async (batchId) => {
    setIsRestoring(true);
    try {
      const response = await api.post(`/data-management/restore-batch/${batchId}`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error('Error restoring batch:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la restauration');
    } finally {
      setIsRestoring(false);
    }
  };

  // Open permanent delete dialog
  const openPermanentDeleteDialog = (batchId, categoryId) => {
    setPermanentDeleteDialog({
      isOpen: true,
      step: 'code',
      batchId,
      categoryId
    });
    setPermanentDeleteCode(['', '', '', '']);
    setPermanentDeleteCodeError(false);
  };

  // Handle permanent delete code input
  const handlePermanentDeleteCodeInput = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...permanentDeleteCode];
    newCode[index] = value.slice(-1);
    setPermanentDeleteCode(newCode);
    setPermanentDeleteCodeError(false);
    
    if (value && index < 3) {
      const nextInput = document.getElementById(`perm-delete-code-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
    
    const fullCode = newCode.join('');
    if (fullCode.length === 4) {
      if (fullCode === ACCESS_CODE) {
        setPermanentDeleteDialog(prev => ({ ...prev, step: 'confirm' }));
      } else {
        setPermanentDeleteCodeError(true);
        setPermanentDeleteCode(['', '', '', '']);
        toast.error('Code incorrect');
        setTimeout(() => {
          const firstInput = document.getElementById('perm-delete-code-0');
          if (firstInput) firstInput.focus();
        }, 100);
      }
    }
  };

  // Handle permanent delete code backspace
  const handlePermanentDeleteKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !permanentDeleteCode[index] && index > 0) {
      const prevInput = document.getElementById(`perm-delete-code-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  // Execute permanent delete
  const executePermanentDelete = async () => {
    const { batchId, categoryId } = permanentDeleteDialog;
    if (!batchId || !categoryId) return;
    
    setIsPermanentlyDeleting(true);
    try {
      await api.delete(`/data-management/permanent-delete/${categoryId}/${batchId}`);
      toast.success('Backup supprimé définitivement');
      setPermanentDeleteDialog({ isOpen: false, step: 'code', batchId: null, categoryId: null });
      fetchData();
    } catch (error) {
      console.error('Error permanently deleting:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression définitive');
    } finally {
      setIsPermanentlyDeleting(false);
    }
  };

  // Close permanent delete dialog
  const closePermanentDeleteDialog = () => {
    if (!isPermanentlyDeleting) {
      setPermanentDeleteDialog({ isOpen: false, step: 'code', batchId: null, categoryId: null });
      setPermanentDeleteCode(['', '', '', '']);
    }
  };

  // Count total backups
  const totalBackups = useMemo(() => {
    let count = 0;
    Object.values(backups).forEach(cat => {
      cat.batches?.forEach(batch => {
        count += batch.items?.length || 0;
      });
    });
    return count;
  }, [backups]);

  const selectedCategoryData = categories.find(c => c.id === selectedCategory);

  // Lock screen - require 4-digit code
  if (!isUnlocked) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="data-management-lock">
        <div className={cn(
          "w-full max-w-sm p-8 rounded-2xl border text-center",
          theme === 'dark' ? 'bg-zinc-900/80 border-white/10' : 'bg-white border-gray-200 shadow-lg'
        )}>
          <div className={cn(
            "w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center",
            theme === 'dark' ? 'bg-red-500/20' : 'bg-red-100'
          )}>
            <Lock size={32} className="text-red-500" />
          </div>
          
          <h2 className={cn("text-xl font-bold mb-2", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
            Accès Sécurisé
          </h2>
          <p className={cn("text-sm mb-6", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
            Entrez le code à 4 chiffres pour accéder à la gestion des données
          </p>
          
          {/* Code Input */}
          <div className="flex justify-center gap-3 mb-4">
            {[0, 1, 2, 3].map((index) => (
              <input
                key={index}
                id={`code-input-${index}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={codeInput[index]}
                onChange={(e) => handleCodeInput(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={cn(
                  "w-14 h-14 text-center text-2xl font-bold rounded-xl border-2 transition-all focus:outline-none focus:ring-2 focus:ring-red-500",
                  codeError
                    ? 'border-red-500 bg-red-500/10 animate-shake'
                    : theme === 'dark'
                      ? 'bg-zinc-800 border-white/20 text-white focus:border-red-500'
                      : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-red-500'
                )}
                autoFocus={index === 0}
              />
            ))}
          </div>
          
          <p className={cn("text-xs", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
            <KeyRound size={12} className="inline mr-1" />
            Contactez un administrateur si vous avez oublié le code
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-zinc-800 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-red-500 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 p-4 md:p-0 pb-24 md:pb-0" data-testid="data-management-page">
      {/* Header - Responsive */}
      <div className="flex flex-col items-center gap-2">
        <div className="flex items-center gap-3">
          <div className={cn(
            "w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center",
            theme === 'dark' ? 'bg-red-500/20' : 'bg-red-100'
          )}>
            <Database size={20} className="md:hidden text-red-500" />
            <Database size={24} className="hidden md:block text-red-500" />
          </div>
          <div>
            <h1 className={cn("text-xl md:text-2xl font-bold", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
              Gestion des Données
            </h1>
            <p className={cn("text-xs md:text-sm", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
              Suppression et restauration
            </p>
          </div>
        </div>
      </div>

      {/* Delete Section - Mobile Optimized */}
      <div className={cn(
        "rounded-xl border p-4 md:p-6",
        theme === 'dark' ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200 shadow-sm'
      )}>
        <div className="flex items-center gap-2 mb-3 md:mb-4">
          <Trash2 size={18} className="text-red-500" />
          <h2 className={cn("text-base md:text-lg font-semibold", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
            Supprimer des données
          </h2>
        </div>
        
        <p className={cn("text-xs md:text-sm mb-3 md:mb-4", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
          Sélectionnez une catégorie. Backup conservé 3 jours.
        </p>

        {/* Categories Grid - 2 cols on mobile, 3 on desktop */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3 mb-4 md:mb-6">
          {categories.map((category) => {
            const Icon = CATEGORY_ICONS[category.id] || Database;
            const isSelected = selectedCategory === category.id;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(isSelected ? '' : category.id)}
                className={cn(
                  "p-3 md:p-4 rounded-xl border transition-all text-left",
                  isSelected
                    ? 'border-red-500 bg-red-500/10'
                    : theme === 'dark'
                      ? 'border-white/10 hover:border-white/20 bg-zinc-800/50'
                      : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                )}
                data-testid={`category-${category.id}`}
              >
                <div className="flex items-center gap-2 md:gap-3">
                  <Icon size={16} className={cn("md:w-5 md:h-5", isSelected ? 'text-red-500' : theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')} />
                  <div className="min-w-0 flex-1">
                    <p className={cn("font-medium text-sm md:text-base truncate", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                      {category.label}
                    </p>
                    <p className={cn("text-xs", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
                      {category.count} élément{category.count !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <Button
          onClick={() => setDeleteDialogOpen(true)}
          disabled={!selectedCategory || selectedCategoryData?.count === 0}
          className="w-full md:w-auto bg-red-500 hover:bg-red-600 text-white"
          data-testid="delete-button"
        >
          <Trash2 size={16} className="mr-2" />
          <span className="truncate">
            Supprimer {selectedCategoryData?.label || 'les données'}
            {selectedCategoryData && ` (${selectedCategoryData.count})`}
          </span>
        </Button>
      </div>

      {/* Restore Section - Mobile Optimized */}
      <div className={cn(
        "rounded-xl border p-4 md:p-6",
        theme === 'dark' ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200 shadow-sm'
      )}>
        {/* Header with responsive layout */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <RotateCcw size={18} className="text-green-500" />
            <h2 className={cn("text-base md:text-lg font-semibold", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
              Restaurer des données
            </h2>
            {totalBackups > 0 && (
              <span className={cn(
                "px-2 py-0.5 rounded-full text-xs",
                theme === 'dark' ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'
              )}>
                {totalBackups}
              </span>
            )}
          </div>
          
          {selectedBackups.length > 0 && (
            <Button
              onClick={handleRestoreSelected}
              disabled={isRestoring}
              className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white text-sm"
              data-testid="restore-selected-button"
            >
              <RotateCcw size={14} className={cn("mr-2", isRestoring && "animate-spin")} />
              Restaurer ({selectedBackups.length})
            </Button>
          )}
        </div>

        {Object.keys(backups).length === 0 ? (
          <div className={cn(
            "text-center py-8 md:py-12 rounded-lg",
            theme === 'dark' ? 'bg-zinc-800/50' : 'bg-gray-50'
          )}>
            <Database size={32} className={cn("mx-auto mb-2", theme === 'dark' ? 'text-zinc-600' : 'text-gray-400')} />
            <p className={cn("text-sm", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>
              Aucun backup disponible
            </p>
            <p className={cn("text-xs mt-1", theme === 'dark' ? 'text-zinc-600' : 'text-gray-400')}>
              Conservés 3 jours après suppression
            </p>
          </div>
        ) : (
          <div className="space-y-3 md:space-y-4">
            {Object.entries(backups).map(([categoryId, categoryData]) => {
              const Icon = CATEGORY_ICONS[categoryId] || Database;
              
              return (
                <div key={categoryId}>
                  <h3 className={cn(
                    "text-xs md:text-sm font-medium mb-2 flex items-center gap-2 uppercase tracking-wide",
                    theme === 'dark' ? 'text-zinc-400' : 'text-gray-600'
                  )}>
                    <Icon size={14} />
                    {categoryData.label}
                  </h3>
                  
                  <div className="space-y-2">
                    {categoryData.batches?.map((batch) => {
                      const isExpanded = expandedBatches[batch._id];
                      const allSelected = batch.items?.every(item => selectedBackups.includes(item.backup_id));
                      const someSelected = batch.items?.some(item => selectedBackups.includes(item.backup_id));
                      
                      return (
                        <div
                          key={batch._id}
                          className={cn(
                            "rounded-lg border overflow-hidden",
                            theme === 'dark' ? 'bg-zinc-800/50 border-white/10' : 'bg-gray-50 border-gray-200'
                          )}
                        >
                          {/* Batch Header - Mobile Optimized */}
                          <div className="p-3">
                            {/* Top row: checkbox, count, expand */}
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <button
                                  onClick={() => selectAllInBatch(batch.items || [])}
                                  className={cn(
                                    "w-5 h-5 flex-shrink-0 rounded border flex items-center justify-center transition-all",
                                    allSelected
                                      ? 'bg-green-500 border-green-500 text-white'
                                      : someSelected
                                        ? 'bg-green-500/30 border-green-500'
                                        : theme === 'dark' ? 'border-white/20' : 'border-gray-300'
                                  )}
                                >
                                  {allSelected && <CheckSquare size={14} />}
                                </button>
                                
                                <p className={cn("text-sm font-medium", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                                  {batch.count} élément{batch.count !== 1 ? 's' : ''}
                                </p>
                              </div>
                              
                              <div className="flex items-center gap-1">
                                <span className={cn(
                                  "text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded-full flex items-center gap-1 whitespace-nowrap",
                                  theme === 'dark' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                                )}>
                                  <Clock size={10} className="hidden sm:block" />
                                  {getTimeRemaining(batch.expires_at)}
                                </span>
                                
                                <button
                                  onClick={() => toggleBatchExpanded(batch._id)}
                                  className={cn(
                                    "p-1.5 rounded-lg",
                                    theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-gray-200'
                                  )}
                                >
                                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                </button>
                              </div>
                            </div>
                            
                            {/* Bottom row: info + buttons */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-2">
                              <div className={cn("text-[10px] md:text-xs", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>
                                <span className="hidden sm:inline">Supprimé par </span>
                                <span className="font-medium">{batch.deleted_by_name}</span>
                                <span className="mx-1">•</span>
                                <span>{formatDateTime(batch.deleted_at)}</span>
                              </div>
                              
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRestoreBatch(batch._id)}
                                  disabled={isRestoring}
                                  className={cn(
                                    "text-xs h-7 flex-1 sm:flex-none",
                                    theme === 'dark' ? 'border-white/10' : ''
                                  )}
                                >
                                  <RotateCcw size={12} className={cn("mr-1", isRestoring && "animate-spin")} />
                                  Restaurer
                                </Button>
                                
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openPermanentDeleteDialog(batch._id, categoryId)}
                                  disabled={isPermanentlyDeleting}
                                  className="text-xs h-7 bg-red-500/10 border-red-500/30 text-red-500 hover:bg-red-500/20 hover:border-red-500"
                                  data-testid={`permanent-delete-${batch._id}`}
                                >
                                  <Trash2 size={12} className="mr-1" />
                                  Effacer
                                </Button>
                              </div>
                            </div>
                          </div>
                          
                          {/* Expanded Items - Mobile Grid */}
                          {isExpanded && (
                            <div className={cn(
                              "border-t px-3 pb-3",
                              theme === 'dark' ? 'border-white/10' : 'border-gray-200'
                            )}>
                              <div className="grid grid-cols-1 gap-2 mt-3">
                                {batch.items?.map((item) => {
                                  const isSelected = selectedBackups.includes(item.backup_id);
                                  return (
                                    <button
                                      key={item.backup_id}
                                      onClick={() => toggleBackupSelection(item.backup_id)}
                                      className={cn(
                                        "p-2.5 md:p-2 rounded-lg border text-left flex items-center gap-2 transition-all active:scale-[0.98]",
                                        isSelected
                                          ? 'border-green-500 bg-green-500/10'
                                          : theme === 'dark'
                                            ? 'border-white/10 hover:border-white/20'
                                            : 'border-gray-200 hover:border-gray-300'
                                      )}
                                    >
                                      <div className={cn(
                                        "w-5 h-5 rounded border flex items-center justify-center flex-shrink-0",
                                        isSelected
                                          ? 'bg-green-500 border-green-500 text-white'
                                          : theme === 'dark' ? 'border-white/20' : 'border-gray-300'
                                      )}>
                                        {isSelected && <CheckSquare size={12} />}
                                      </div>
                                      <span className={cn(
                                        "text-sm truncate",
                                        theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'
                                      )}>
                                        {item.display_name || 'Sans nom'}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className={cn(
          "max-w-md",
          theme === 'dark' ? 'bg-zinc-900 border-white/10' : 'bg-white'
        )}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
              <AlertTriangle className="text-red-500" size={20} />
              Confirmer la suppression
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className={cn("text-sm", theme === 'dark' ? 'text-zinc-400' : 'text-gray-600')}>
              Vous êtes sur le point de supprimer :
            </p>
            
            <div className={cn(
              "p-4 rounded-lg",
              theme === 'dark' ? 'bg-red-500/10 border border-red-500/30' : 'bg-red-50 border border-red-200'
            )}>
              <p className={cn("font-semibold text-lg", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                {selectedCategoryData?.count || 0} {selectedCategoryData?.label?.toLowerCase()}
              </p>
            </div>
            
            <div className={cn(
              "p-3 rounded-lg text-sm",
              theme === 'dark' ? 'bg-zinc-800 text-zinc-300' : 'bg-gray-100 text-gray-700'
            )}>
              <p className="flex items-center gap-2">
                <Clock size={16} className="text-yellow-500" />
                Un backup sera conservé pendant <strong>3 jours</strong>
              </p>
            </div>
            
            <p className={cn("text-xs", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>
              Cette action est irréversible après l'expiration du backup.
            </p>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
              className={theme === 'dark' ? 'border-white/10' : ''}
            >
              Annuler
            </Button>
            <Button
              onClick={handleDeleteCategory}
              disabled={isDeleting}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {isDeleting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <>
                  <Trash2 size={16} className="mr-2" />
                  Supprimer définitivement
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanent Delete Dialog */}
      <Dialog open={permanentDeleteDialog.isOpen} onOpenChange={closePermanentDeleteDialog}>
        <DialogContent className={cn(
          "max-w-md",
          theme === 'dark' ? 'bg-zinc-900 border-white/10' : 'bg-white'
        )}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
              <Trash2 className="text-red-500" size={20} />
              Effacement Définitif
            </DialogTitle>
          </DialogHeader>
          
          {permanentDeleteDialog.step === 'code' ? (
            /* Step 1: Code verification */
            <div className="space-y-4 py-4">
              <div className={cn(
                "p-4 rounded-lg text-center",
                theme === 'dark' ? 'bg-red-500/10 border border-red-500/30' : 'bg-red-50 border border-red-200'
              )}>
                <AlertTriangle size={32} className="mx-auto mb-2 text-red-500" />
                <p className={cn("font-semibold", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                  Attention : Action irréversible
                </p>
                <p className={cn("text-sm mt-1", theme === 'dark' ? 'text-zinc-400' : 'text-gray-600')}>
                  Le backup sera supprimé définitivement
                </p>
              </div>
              
              <p className={cn("text-sm text-center", theme === 'dark' ? 'text-zinc-400' : 'text-gray-600')}>
                Entrez le code à 4 chiffres pour continuer
              </p>
              
              <div className="flex justify-center gap-3">
                {[0, 1, 2, 3].map((index) => (
                  <input
                    key={index}
                    id={`perm-delete-code-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={permanentDeleteCode[index]}
                    onChange={(e) => handlePermanentDeleteCodeInput(index, e.target.value)}
                    onKeyDown={(e) => handlePermanentDeleteKeyDown(index, e)}
                    className={cn(
                      "w-12 h-12 text-center text-xl font-bold rounded-xl border-2 transition-all focus:outline-none focus:ring-2 focus:ring-red-500",
                      permanentDeleteCodeError
                        ? 'border-red-500 bg-red-500/10 animate-shake'
                        : theme === 'dark'
                          ? 'bg-zinc-800 border-white/20 text-white focus:border-red-500'
                          : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-red-500'
                    )}
                    autoFocus={index === 0}
                  />
                ))}
              </div>
            </div>
          ) : (
            /* Step 2: Final confirmation */
            <div className="space-y-4 py-4">
              <div className={cn(
                "p-6 rounded-lg text-center",
                theme === 'dark' ? 'bg-red-500/20 border border-red-500/50' : 'bg-red-100 border border-red-300'
              )}>
                <AlertTriangle size={48} className="mx-auto mb-3 text-red-500" />
                <p className={cn("font-bold text-lg", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                  CETTE ACTION EST IRRÉVERSIBLE
                </p>
                <p className={cn("text-sm mt-2", theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>
                  Le backup et toutes les données qu'il contient seront supprimés définitivement.
                  <br />
                  <strong>Aucune récupération possible.</strong>
                </p>
              </div>
              
              <p className={cn("text-center font-medium", theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>
                Êtes-vous absolument sûr de vouloir continuer ?
              </p>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={closePermanentDeleteDialog}
                  disabled={isPermanentlyDeleting}
                  className={cn("flex-1", theme === 'dark' ? 'border-white/10' : '')}
                >
                  Non, annuler
                </Button>
                <Button
                  onClick={executePermanentDelete}
                  disabled={isPermanentlyDeleting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold"
                >
                  {isPermanentlyDeleting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <>
                      <Trash2 size={16} className="mr-2" />
                      Oui, effacer
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
